/**
 * Pure view helpers for Settings → AI Connections.
 * Never invents percentages; Unknown stays Unknown.
 */

import type { AiConnectionDisplay, AiConnectionMeterMode, AiConnectionMode } from './ai-connections-catalog';
import type { ProviderUsageLatest } from './data';

export type AiAuthState = 'signed_in' | 'signed_out' | 'unknown';

export interface AiConnectionStatusRow {
  readonly org_id: string;
  readonly node_id: string;
  readonly connection_key: string;
  readonly installed: boolean;
  readonly auth_state: AiAuthState;
  readonly capability_note: string | null;
  readonly meter_mode: AiConnectionMeterMode;
  readonly connection_mode?: AiConnectionMode | null;
  readonly last_probe_at: string;
  readonly last_error: string | null;
}

export type AiConnectionSourceLabel =
  | 'Measured from provider/CLI'
  | 'Measured from Brunel runs'
  | 'Connected, capacity unavailable'
  | 'Local / Unlimited'
  | 'Not probed yet'
  | 'Not installed';

export interface AiConnectionRowView {
  readonly connectionKey: string;
  readonly displayLabel: string;
  readonly meterLabel: string;
  readonly installed: boolean | null;
  readonly authState: AiAuthState | null;
  readonly note: string | null;
  readonly sourceLabel: AiConnectionSourceLabel;
  readonly lastProbeAt: string | null;
  readonly lastReading: string | null;
  readonly lastError: string | null;
  readonly canConnect: boolean;
  readonly canRefreshUsage: boolean;
  readonly meterMode: AiConnectionMeterMode;
  readonly connectionMode: AiConnectionMode;
}

function formatReading(row: ProviderUsageLatest | null): string | null {
  if (row === null) return null;
  const parts: string[] = [];
  if (row.measure_unit === 'percent') {
    if (row.used_value !== null) parts.push(`${row.used_value}% used`);
    else if (row.remaining_value !== null) parts.push(`${row.remaining_value}% remaining`);
  } else if (row.measure_unit === 'tokens' && row.used_value !== null) {
    parts.push(`${row.used_value} tokens spent`);
  } else if (row.used_value !== null) {
    parts.push(`${row.used_value} ${row.measure_unit}`);
  } else if (row.remaining_value !== null) {
    parts.push(`${row.remaining_value} ${row.measure_unit} remaining`);
  } else if (row.resets_at) {
    parts.push(`resets ${row.resets_at}`);
  }
  if (row.resets_at && row.used_value !== null) parts.push(`resets ${row.resets_at}`);
  return parts.length === 0 ? null : parts.join(' · ');
}

function matchUsage(
  display: AiConnectionDisplay,
  usage: readonly ProviderUsageLatest[],
  nodeId: string | null,
): ProviderUsageLatest | null {
  const poolExact = usage.filter((row) =>
    row.provider_key === display.providerKey
    && row.pool_key === display.poolKey
    && (nodeId === null || row.node_id === null || row.node_id === nodeId));
  if (poolExact.length === 0) return null;
  return poolExact.reduce((a, b) => (a.observed_at >= b.observed_at ? a : b));
}

function sourceFor(input: {
  readonly display: AiConnectionDisplay;
  readonly installed: boolean | null;
  readonly authState: AiAuthState | null;
  readonly reading: string | null;
  readonly usage: ProviderUsageLatest | null;
}): AiConnectionSourceLabel {
  if (input.installed === false) return 'Not installed';
  if (input.display.meterMode === 'local_unlimited' && input.installed === true) {
    return 'Local / Unlimited';
  }
  if (input.reading !== null && input.usage !== null) {
    if (input.usage.source === 'cli_output' || input.usage.source === 'provider_api') {
      return 'Measured from provider/CLI';
    }
    return 'Measured from Brunel runs';
  }
  if (input.installed === true && (
    input.authState === 'signed_in'
    || input.display.defaultNote !== null
    || input.display.meterMode === 'unknown'
  )) {
    return 'Connected, capacity unavailable';
  }
  if (input.installed === null && input.authState === null) return 'Not probed yet';
  if (input.installed === true) return 'Connected, capacity unavailable';
  return 'Not probed yet';
}

export function buildAiConnectionRows(input: {
  readonly catalog: readonly AiConnectionDisplay[];
  readonly statuses: readonly AiConnectionStatusRow[];
  readonly usage: readonly ProviderUsageLatest[];
  readonly nodeId: string | null;
}): readonly AiConnectionRowView[] {
  return input.catalog.map((display) => {
    const status = input.statuses.find((row) =>
      row.connection_key === display.connectionKey
      && (input.nodeId === null || row.node_id === input.nodeId)) ?? null;
    const usage = matchUsage(display, input.usage, input.nodeId);
    const installed = status?.installed ?? null;
    const authState = status?.auth_state ?? null;
    const reading = formatReading(usage);
    return {
      connectionKey: display.connectionKey,
      displayLabel: display.displayLabel,
      meterLabel: display.meterLabel,
      installed,
      authState,
      note: status?.capability_note ?? (installed === true ? display.defaultNote : null),
      sourceLabel: sourceFor({ display, installed, authState, reading, usage }),
      lastProbeAt: status?.last_probe_at ?? null,
      lastReading: reading,
      lastError: status?.last_error ?? null,
      canConnect: display.canConnect,
      canRefreshUsage: display.canRefreshUsage,
      meterMode: display.meterMode,
      connectionMode: status?.connection_mode ?? display.connectionMode,
    };
  });
}

export function authStateLabel(state: AiAuthState | null): string {
  if (state === 'signed_in') return 'Signed in';
  if (state === 'signed_out') return 'Signed out';
  if (state === 'unknown') return 'Unknown';
  return '—';
}

export function installedLabel(installed: boolean | null): string {
  if (installed === true) return 'Installed';
  if (installed === false) return 'Not installed';
  return 'Unknown';
}


export function connectionModeLabel(mode: AiConnectionMode): string {
  if (mode === 'commandos_vault') return 'CommandOS vault';
  if (mode === 'node_bound') return 'Bound to this computer';
  if (mode === 'local_no_auth') return 'Local · no account';
  return 'Explicit API key (metered)';
}
